import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Truck, HeadphonesIcon, WhatsApp, MapPin, Clock, Phone } from '../components/icons';
import HeroCarousel from '../components/HeroCarousel';
import FeaturedProductsCarousel from '../components/FeaturedProductsCarousel';
import EventsSection from '../components/EventsSection';
import BlogSection from '../components/BlogSection';
import PartnersSection from '../components/PartnersSection';
import { CATALOGUE_ENABLED } from '../config/features';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

const Home = () => {
  const { data: featuredProducts, isLoading, error } = useQuery({
    queryKey: ['featured-products'],
    queryFn: async () => {
      const response = await api.get('/products?featured=true&limit=10');
      // Filter out specific products and ensure Te-Econom Bond is included
      if (response.data && response.data.data) {
        const filteredProducts = response.data.data.filter((product: any) => {
          const productName = product.name.toLowerCase();
          return !productName.includes('ivoclar ips e.max') && 
                 !productName.includes('ivoclar tetric evoflow');
        });
        
        // Check if Te-Econom Bond is in the filtered results
        const teEconomBond = filteredProducts.find((product: any) => 
          product.name.toLowerCase().includes('te-econom bond')
        );
        
        // If Te-Econom Bond is not in featured products, try to get it from all products
        if (!teEconomBond) {
          try {
            const allProductsResponse = await api.get('/products?limit=50');
            const teEconomBondFromAll = allProductsResponse.data.data.find((product: any) => 
              product.name.toLowerCase().includes('te-econom bond')
            );
            if (teEconomBondFromAll) {
              filteredProducts.unshift(teEconomBondFromAll); // Add to beginning
            }
          } catch (error) {
            console.log('Could not fetch Te-Econom Bond from all products');
          }
        }
        
        return {
          ...response.data,
          data: filteredProducts.slice(0, 4) // Take first 4 after filtering
        };
      }
      return response.data;
    },
  });

  const features = [
    {
      icon: Shield,
      title: 'Quality Assured',
      description: 'All products are sourced from leading manufacturers with quality certifications',
    },
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Quick delivery across East Africa with reliable shipping partners',
    },
    {
      icon: HeadphonesIcon,
      title: 'Expert Support',
      description: '24/7 technical support from dental professionals',
    },
  ];

  const stats = [
    { number: '500+', label: 'Products' },
    { number: '50+', label: 'Clinics Served' },
    { number: '5', label: 'Countries' },
    { number: '98%', label: 'Satisfaction' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative" data-hero>
        <HeroCarousel />
      </section>

      {/* Amalgam Replacement Section - Hero-Style Impact Block */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gradient-to-br from-[#0D56A6]/5 via-white to-[#8EC641]/5 relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(13,86,166,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(142,198,65,0.1),transparent_50%)]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
            {/* Left Column - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              {/* Badge */}
              <div className="mb-4 sm:mb-6">
                <span className="inline-block bg-[#8EC641] text-white text-xs sm:text-sm font-bold px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-lg">
                  Mercury-Free Initiative
                </span>
              </div>
              
              {/* Headline */}
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                Leading Tanzania's{' '}
                <span className="text-[#0D56A6]">Mercury-Free Dentistry</span>{' '}
                Movement
              </h2>
              
              {/* Subheadline */}
              <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed font-light">
                In partnership with the <strong>Ministry of Health</strong> and <strong>MSD</strong>, 
                Dentopharma is replacing amalgam with safer, bioactive Cention N across hospitals 
                and clinics nationwide.
              </p>
              
              {/* Partner Logos */}
              <div className="flex items-center justify-center sm:justify-start space-x-4 sm:space-x-6 mb-4 sm:mb-6">
                <img 
                  src="/images/Global Partners/Logos/Ministry of health.jpeg" 
                  alt="Ministry of Health Logo"
                  className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                />
                <img 
                  src="/images/Global Partners/Logos/MSD logo.svg" 
                  alt="MSD Logo"
                  className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                />
                <img 
                  src="/images/Global Partners/Logos/ivoclar-logo.svg" 
                  alt="Ivoclar Logo"
                  className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                />
              </div>
              
              {/* Impact Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="text-center p-2 sm:p-3 bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm">
                  <div className="text-lg sm:text-xl font-bold text-[#0D56A6] mb-1">100+</div>
                  <div className="text-xs text-gray-600 font-medium">Clinics</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm">
                  <div className="text-lg sm:text-xl font-bold text-[#8EC641] mb-1">✓</div>
                  <div className="text-xs text-gray-600 font-medium">MoH Approved</div>
                </div>
                <div className="text-center p-2 sm:p-3 bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm">
                  <div className="text-lg sm:text-xl font-bold text-[#0D56A6] mb-1">1000s</div>
                  <div className="text-xs text-gray-600 font-medium">Patients</div>
                </div>
              </div>
              
              {/* CTA Button */}
              <Link
                to="/products/cention-n"
                className="inline-flex items-center justify-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-bold text-sm sm:text-base px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                See How We're Transforming Dentistry
                <ArrowRight size={14} className="sm:w-4 sm:h-4 ml-1.5" />
              </Link>
            </motion.div>
            
            {/* Right Column - Visual Elements */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative order-1 lg:order-2"
            >
              {/* Main Product Image */}
              <div className="mb-4 sm:mb-6">
                <img 
                  src="/images/cention n.png" 
                  alt="Cention N - Mercury-Free Filling Material"
                  className="w-full h-auto rounded-xl sm:rounded-2xl shadow-lg max-w-sm mx-auto lg:max-w-none"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products Section (hidden while the Catalogue is in development) */}
      {CATALOGUE_ENABLED && (
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Featured Products
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our most popular dental supplies and equipment from world-renowned manufacturers
            </p>
          </motion.div>

          <FeaturedProductsCarousel 
            products={featuredProducts?.data || []}
            isLoading={isLoading}
            error={error}
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link
              to="/catalogue"
              className="inline-flex items-center justify-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>View All Products</span>
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      )}

                {/* Events Section */}
          <EventsSection />

          {/* Blog Section */}
          <BlogSection />

          {/* Partners Section */}
          <PartnersSection />

          {/* Why Choose Dentopharma Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Dentopharma?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Leading distributor of premium dental supplies across East Africa
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                  <feature.icon size={40} className="text-[#0D56A6] group-hover:text-[#8EC641] transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0D56A6]">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-primary-100">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>




      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Ready to Transform Your Dental Practice?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Join hundreds of dental professionals across East Africa who trust Dentopharma 
              for their supplies and equipment needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {CATALOGUE_ENABLED && (
                <Link to="/catalogue" className="btn-primary">
                  Explore Catalogue
                </Link>
              )}
              <Link to="/contact" className="btn-outline">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Find Us
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Visit our office in the heart of Dar es Salaam for personalized service and expert advice.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Google Map */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl overflow-hidden shadow-lg"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3962.123456789!2d39.2839876!3d-6.8165625!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c4b0064eacf0d%3A0x22f9eb04acd3723f!2sDentopharma%20supplies!5e0!3m2!1sen!2stz!4v1234567890123!5m2!1sen!2stz"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Dentopharma Location Map"
                className="w-full h-[500px]"
              ></iframe>
            </motion.div>

            {/* Location Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Visit Our Office
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#0D56A6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-[#0D56A6]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        Address
                      </h4>
                      <p className="text-gray-600">
                        India Street, Dar es Salaam, Tanzania
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#0D56A6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock size={24} className="text-[#0D56A6]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        Business Hours
                      </h4>
                      <p className="text-gray-600">
                        Monday - Friday: 8:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 2:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#0D56A6]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-[#0D56A6]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        Contact
                      </h4>
                      <p className="text-gray-600">
                        Phone: +255 754 690 738<br />
                        Email: info@dentopharma.co.tz
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-[#0D56A6] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0A4A8F] transition-colors duration-300"
                >
                  Get Directions
                  <ArrowRight size={20} className="ml-2" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

              {/* Floating WhatsApp Button */}
        <motion.a
          href="https://wa.me/255754690738?text=Hi%20DentoPharma!%20I%20would%20like%20to%20learn%20more%20about%20your%20dental%20products."
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.6 }}
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.95 }}
          className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 w-12 h-12 sm:w-16 sm:h-16 bg-[#25D366] hover:bg-[#20BA5A] rounded-full flex items-center justify-center text-white shadow-2xl hover:shadow-[#25D366]/30 transition-all duration-300 z-50 border-2 border-white/20 cursor-pointer"
          title="Chat with us on WhatsApp"
        >
          <WhatsApp size={24} className="sm:w-8 sm:h-8" />
        </motion.a>
    </div>
  );
};

export default Home;
